<template>
    <page :tabs="tabs" :busy="busy">
        <grid ref="grid" :columnDefs="columnDefs" :rowData="items">
            <responsive-button
                :icon="['fas', 'plus']"
                variant="primary"
                @click="$bvModal.show('create-edit-package-modal')"
                >Add Package</responsive-button
            >
        </grid>
        <create-edit-package-modal
            ref="form"
            modalTitle="Create Package"
            modalId="create-edit-package-modal"
            @submit="createPackage"
        ></create-edit-package-modal>
    </page>
</template>

<script>
import API from '../../../api';
import isEmpty from 'lodash/isEmpty';
import Grid from '../../generic/grid/grid.vue';
import page from '../../generic/page/page.vue';
import ResponsiveButton from '../../generic/responsive-button/responsive-button.vue';
import {
    ADMIN_SUBSCRIPTION_PACKAGES_ENDPOINT,
    ADMIN_SUBSCRIPTION_PACKAGES_UPDATE_DELETE_ENDPOINT,
} from '../../../endpoints';
import ActionsCellRenderer from './actions-cell-renderer.vue';
import { formatResponseError } from '../../../utils/api';
import CreateEditPackageModal from './create-edit-package-modal.vue';

export default {
    components: { page, Grid, ResponsiveButton, CreateEditPackageModal, actionsCellRenderer: ActionsCellRenderer, },
    data() {
        return {
            tabs: [{ title: 'Packages', active: true }],
            busy: null,
            items: [],
        };
    },
    methods: {
        async fetchData() {
            try {
                this.busy = true;
                const response = await API.get(
                    ADMIN_SUBSCRIPTION_PACKAGES_ENDPOINT()
                );
                this.items = response.data;
            } catch (e) {
                this.errorToast({ text: 'Could not load packages' });
            } finally {
                this.busy = false;
            }
        },
        async createPackage(value) {
            try {
                this.busy = true;
                await API.post(ADMIN_SUBSCRIPTION_PACKAGES_ENDPOINT(), value);
                this.$bvModal.hide('create-edit-package-modal');
                await this.fetchData();
            } catch (err) {
                const { generic, fields } = formatResponseError(err);
                if (generic) {
                    this.errorToast({
                        text: generic,
                    });
                }
                if (!isEmpty(fields)) {
                    this.$refs.form.$children[0].setErrors(fields);
                }
            } finally {
                this.busy = false;
            }
        },
    },
    mounted() {
        this.fetchData();
    },
    computed: {
        columnDefs() {
            return [
                {
                    field: 'name',
                    headerName: 'Name',
                    maxWidth: 250,
                },
                {
                    field: 'description',
                    headerName: 'Description',
                },
                {
                    field: 'trial_period',
                    headerName: 'Trial Period',
                    cellRenderer: ({ data }) =>
                        `${data.trial_period} ${data.trial_period_unit}s`,
                    maxWidth: 150,
                },
                {
                    field: 'billing_period',
                    headerName: 'Subscription Length',
                    cellRenderer: ({ data }) =>
                        `${data.billing_period} ${data.billing_period_unit}s`,
                    maxWidth: 150,
                },
                {
                    field: 'max_properties',
                    headerName: 'Max Properties',
                    cellRenderer: ({ data }) => {
                        return data.max_properties || 'Unlimited';
                    },
                    maxWidth: 150,
                },
                {
                    field: 'amount',
                    headerName: 'Amount($)',
                    maxWidth: 120,
                },
                {
                    field: 'current_subscribers_count',
                    headerName: 'Current Subscribers',
                    maxWidth: 120,

                    cellRenderer: ({ data }) => {
                        const link = `${window.location.origin}/app/subscriber/admin/subscriptions/?package=${data.id}`;
                        return data.current_subscribers_count
                            ? `<a href=${link}>${data.current_subscribers_count}</a>`
                            : data.current_subscribers_count;
                    },
                },
                {
                    field: 'status',
                    headerName: 'Status',
                    maxWidth: 120,
                },
                {
                    field: '',
                    cellRenderer: 'actionsCellRenderer',
                    minWidth: 130,
                    maxWidth: 130,
                    cellRendererParams: {
                        deletePackage: async (id) => {
                            try {
                                await API.delete(
                                    ADMIN_SUBSCRIPTION_PACKAGES_UPDATE_DELETE_ENDPOINT(
                                        id
                                    )
                                );
                                await this.fetchData();
                            } catch (e) {
                                this.errorToast({
                                    text: 'Could not delete package',
                                });
                            }
                        },
                        updatePackage: async (id, value, modalId, formRef) => {
                            try {
                                await API.patch(
                                    ADMIN_SUBSCRIPTION_PACKAGES_UPDATE_DELETE_ENDPOINT(
                                        id
                                    ),
                                    value
                                );
                                this.$bvModal.hide(modalId);
                                await this.fetchData();
                            } catch (err) {
                                const { generic, fields } = formatResponseError(
                                    err
                                );
                                if (generic) {
                                    this.errorToast({
                                        text: generic,
                                    });
                                }
                                if (!isEmpty(fields)) {
                                    formRef.$children[0].setErrors(fields);
                                }
                            }
                        },
                    },
                },
            ];
        },
    },
};
</script>